"use strict";
/********************************************************************************
  HxCollectionStore.ts
 
  Copyright (c) Microsoft Corporation. All rights reserved.
 ********************************************************************************/
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spread = (this && this.__spread) || function () {
    for (var ar = [], i = 0; i < arguments.length; i++) ar = ar.concat(__read(arguments[i]));
    return ar;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HxCollectionStore = void 0;
var HxCollectionBase_1 = __importDefault(require("./HxCollectionBase"));
var HxCollectionChange_1 = require("./HxCollectionChange");
var HxCollectionStore = /** @class */ (function () {
    function HxCollectionStore(objectStore, nativeInterop) {
        this.collections = new Map();
        this.objectStore = objectStore;
        this.nativeInterop = nativeInterop;
    }
    HxCollectionStore.prototype.hasCollection = function (collectionId) {
        return this.collections.has(collectionId.getCompactIDString());
    };
    // Get a collection from the active set
    HxCollectionStore.prototype.findCollection = function (collectionId) {
        // Is there a way to assert object type?
        var entry = this.collections.get(collectionId.getCompactIDString());
        return entry ? entry.collection : null;
    };
    // Return value indicates whether anything changed
    HxCollectionStore.prototype.onCollectionLoaded = function (collection) {
        var _this = this;
        var entry = this.collections.get(collection.objectId.getCompactIDString());
        if (entry) {
            // Need to AddRef all objects per session
            var allSessionsIds_1 = Array.from(entry.sessionIds.keys());
            collection.items.forEach(function (object) {
                allSessionsIds_1.forEach(function (sessionId) { return _this.objectStore.addRefObject(object.objectId.getCompactIDString(), sessionId); });
                _this.objectStore.updateObject(object);
            });
            // Assert !entry.collection -- which is not currently true for time virtualized collection
            // Release our references on each item in the previous version of the collection for (all active session)
            if (entry.collection) {
                entry.collection.items.forEach(function (object) {
                    allSessionsIds_1.forEach(function (sessionId) { return _this.objectStore.releaseObject(object.objectId.getCompactIDString(), sessionId); });
                });
            }
            entry.collection = collection;
            return true;
        }
        else {
            // If the entry is missing, the client might have released the collection in a race with load/notification.
            // Make sure hxcore knows this collection is not part js active set.
            this.nativeInterop.deactivateObject(collection.objectId, collection.storageSequenceNumber);
            return false;
        }
    };
    // Return value indicates whether anything changed
    HxCollectionStore.prototype.onCollectionChanged = function (update) {
        var _this = this;
        var entry = this.collections.get(update.collectionId.getCompactIDString());
        if (entry) {
            // Assert entry.collection
            if (entry.collection) {
                if (update.storageSequenceNumber < entry.collection.storageSequenceNumber) {
                    // log(`onCollectionChanged (${entry.collection.objectId}, ${entry.collection.objectType}) rejected due to insufficient storage sequence number: ${update.storageSequenceNumber} < ${entry.collection.storageSequenceNumber}`);
                    return [];
                }
                var allSessionsIds_2 = Array.from(entry.sessionIds);
                // Make a new collection rather than mutating the existing one to avoid invalidating shallow compare
                var newItems_1 = __spread(entry.collection.items);
                if (update.storageSequenceNumber !== 0) {
                    entry.collection.storageSequenceNumber = update.storageSequenceNumber;
                }
                update.changes.forEach(function (change) {
                    switch (change.type) {
                        case HxCollectionChange_1.HxCollectionChangeType.ItemChanged:
                            // Update the object in both the collection and the object store.
                            // Assert change.index < newItems.length && newItems[change.index] == change.object.objectId;
                            if (change.index < newItems_1.length) {
                                newItems_1.splice(change.index, 1, change.object);
                                _this.objectStore.updateObject(change.object);
                            }
                            break;
                        case HxCollectionChange_1.HxCollectionChangeType.ItemCreated:
                            // Add the new item to the collection, take a reference to the object for
                            // for the collection, and add the object itself to the object store.
                            newItems_1.splice(change.index, 0, change.object);
                            allSessionsIds_2.forEach(function (sessionId) { return _this.objectStore.addRefObject(change.objectId.getCompactIDString(), sessionId); });
                            _this.objectStore.updateObject(change.object);
                            break;
                        case HxCollectionChange_1.HxCollectionChangeType.ItemRemoved:
                            // Remove the object from the collection and drop the collection's reference
                            // to the object
                            // Assert change.index < newItems.length && newItems[change.index] == change.objectId;
                            if (change.index < newItems_1.length) {
                                newItems_1.splice(change.index, 1);
                                allSessionsIds_2.forEach(function (sessionId) { return _this.objectStore.releaseObject(change.objectId.getCompactIDString(), sessionId); });
                            }
                            break;
                    }
                });
                var newCollection = new HxCollectionBase_1.default(entry.collection.objectId, entry.collection.objectType, newItems_1, entry.collection.storageSequenceNumber, entry.collection.activeSet);
                entry.collection = newCollection;
                return allSessionsIds_2;
            }
        }
        return [];
    };
    HxCollectionStore.prototype.addRefCollection = function (collectionCompactId, sessionId) {
        var _this = this;
        var entry = this.collections.get(collectionCompactId);
        if (entry) {
            if (entry.sessionIds.has(sessionId)) {
                throw new Error("Unexpected call to addRefCollection of existing collection"); // Internal invariant violation
            }
            entry.sessionIds.add(sessionId);
            if (entry.collection) {
                // Any time a new session takes a ref on the collection, it implicitly takes a ref on all items in that collection
                entry.collection.items.forEach(function (object) {
                    _this.objectStore.addRefObject(object.objectId.getCompactIDString(), sessionId);
                });
            }
        }
        else {
            this.collections.set(collectionCompactId, { collection: undefined, sessionIds: new Set([sessionId]) });
        }
    };
    HxCollectionStore.prototype.releaseCollection = function (collectionCompactId, sessionId) {
        var _this = this;
        var entry = this.collections.get(collectionCompactId);
        // Assert entry
        if (entry) {
            var removed = entry.sessionIds.delete(sessionId);
            if (!removed) {
                throw new Error("Unexpected releaseCollection with no existing collection"); // Internal invariant violation
            }
            if (entry.collection) {
                // Any time a session drops all refs on the collection, it implicitly drops a ref on all items in that collection
                entry.collection.items.forEach(function (object) {
                    _this.objectStore.releaseObject(object.objectId.getCompactIDString(), sessionId);
                });
            }
            if (entry.sessionIds.size === 0) {
                this.collections.delete(collectionCompactId);
            }
        }
    };
    return HxCollectionStore;
}());
exports.HxCollectionStore = HxCollectionStore;
exports.default = HxCollectionStore;
